import { useEffect, useState } from 'react';

import { PIPELINE_STAGES, type ContractDetail, type PipelineStage, type PipelineTimelineStep } from '@clm/shared-types';

import { fetchPipelineStatus } from '../api/pipeline-status';
import { stepStates, trackerNote, type StepState } from './pipeline-stage-state';
import { processingLabel } from './trust-labels';

/**
 * The five-step pipeline tracker on the Contract record — CLM-70 (D-70-5).
 *
 * **Pending → Converted → Pseudonymised → Extracted → Settled.** The shape
 * renders at once from `contract.processing_status` (the record already has
 * it), then `GET /v1/admin/pipeline/status?contract_key=` fills in when each
 * step was reached. If that call fails the rail still renders and says "times
 * unavailable" — the tracker never blocks the record.
 *
 * The last step is the pipeline stage `Verified`, and it is labelled
 * **"Settled"** (CLM-108, R-108-14): the trust advance writes it when nothing is
 * outstanding, which is also true of a contract some of whose fields were
 * auto-approved by policy and read by nobody. Whether a person read every field
 * is the trust axis (`data_trust`), which the note under the rail states.
 *
 * ## What each status looks like
 *
 * - `PendingExtraction` — the first step is current, nothing reached.
 * - `Converted` / `Pseudonymised` / `Extracted` — done up to there, the next
 *   step current.
 * - `PendingVerification` — Extracted done, Settled current: *awaiting human
 *   verification (FR-07)*.
 * - `Verified` — all five done. With `data_trust = 'AutoApproved'` the note
 *   says some fields were settled by policy and not reviewed by a person (CR-06).
 * - `ExtractionFailed` — the blocked step comes from `ai_processing`: a refusal
 *   or a plain failure blocks **Extracted**; a §4A.5 hold (the runner's
 *   `HOLD_PREFIX` in `failure_reason`) blocks **Pseudonymised**. The reason is
 *   the audit row's text, already on the record (CR-01).
 * - `manual_entry_pending` — Converted done, Pseudonymised and Extracted
 *   **skipped**: *Withheld — processing restricted (§4C)*. Not a failure.
 *
 * No PartyPicker, no filter, no button: this is a read of one contract's state.
 * The shape derivation lives in `pipeline-stage-state.ts`.
 */

/** Each stage is a processing status, so it reads through the shared pipeline label (`Verified` → "Settled"). */
const LABELS: Readonly<Record<PipelineStage, string>> = {
  PendingExtraction: processingLabel('PendingExtraction'),
  Converted: processingLabel('Converted'),
  Pseudonymised: processingLabel('Pseudonymised'),
  Extracted: processingLabel('Extracted'),
  Verified: processingLabel('Verified'),
};

type Times =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly steps: readonly PipelineTimelineStep[] }
  | { readonly kind: 'unavailable' };

export function PipelineStageTracker({ contract }: { readonly contract: ContractDetail }) {
  const [times, setTimes] = useState<Times>({ kind: 'loading' });
  const states = stepStates(contract);
  const note = trackerNote(contract);

  useEffect(() => {
    const controller = new AbortController();
    setTimes({ kind: 'loading' });
    void (async () => {
      try {
        const response = await fetchPipelineStatus({ contract_key: contract.contract_key }, controller.signal);
        if (controller.signal.aborted) return;
        setTimes(response.timeline === null ? { kind: 'unavailable' } : { kind: 'ready', steps: response.timeline });
      } catch {
        if (controller.signal.aborted) return;
        setTimes({ kind: 'unavailable' });
      }
    })();
    return () => {
      controller.abort();
    };
  }, [contract.contract_key]);

  const reached = (stage: PipelineStage): string | null =>
    times.kind === 'ready' ? (times.steps.find((s) => s.stage === stage)?.reached_utc ?? null) : null;

  return (
    <div className="stages-wrap" data-testid="pipeline-stage-tracker">
      <ol className="stages" aria-label="Pipeline stages">
        {PIPELINE_STAGES.map((stage, index) => {
          const state = states[index] ?? 'todo';
          const at = reached(stage);
          return (
            <li key={stage} data-state={state} data-stage={stage}>
              <span className="stage-label">{LABELS[stage]}</span>
              {at !== null ? (
                <time dateTime={at}>{at}</time>
              ) : (
                <small className="mut">
                  {state === 'done' && times.kind === 'unavailable' ? 'times unavailable' : STATE_COPY[state]}
                </small>
              )}
            </li>
          );
        })}
      </ol>
      {times.kind === 'unavailable' ? <p className="mut stage-note">Stage times unavailable — the status read did not complete.</p> : null}
      {note !== null ? <p className="mut stage-note">{note}</p> : null}
    </div>
  );
}

const STATE_COPY: Readonly<Record<StepState, string>> = {
  done: 'reached',
  current: 'current',
  blocked: 'blocked',
  skipped: 'skipped',
  todo: 'not yet',
};
